import { create } from 'zustand'
import {
    MARKET_HOURS,
    PLAYBACK_SPEED_CONFIG,
    DEFAULT_PLAYBACK_SPEED,
    STORAGE_KEYS,
} from '../config/constants'

interface PlaybackState {
    isPlaying: boolean
    togglePlayback: () => void
    setPlaying: (playing: boolean) => void
    snapshotMin: number
    setSnapshotMin: (min: number) => void
    availableMinutes: number[]
    setAvailableMinutes: (mins: number[]) => void
    isRenderingFrame: boolean
    setRenderingFrame: (isRendering: boolean) => void
    stepForward: () => void
    stepBackward: () => void
    jumpTime: (minutesDelta: number) => void
    playSpeed: number
    setPlaySpeed: (speed: number) => void
}

let animationFrameId: number | null = null
let lastTickTime = 0
let lastFrameDispatchTime = 0

// Fast binary search to find the index of the closest element to target
function findClosestIndex(arr: number[], target: number): number {
    if (arr.length === 0) return -1
    let left = 0
    let right = arr.length - 1
    let bestIdx = 0
    let bestDiff = Math.abs(arr[0]! - target)

    while (left <= right) {
        const mid = (left + right) >> 1
        const midVal = arr[mid]!
        const diff = Math.abs(midVal - target)

        if (diff < bestDiff) {
            bestDiff = diff
            bestIdx = mid
        }

        if (midVal === target) {
            return mid
        } else if (midVal < target) {
            left = mid + 1
        } else {
            right = mid - 1
        }
    }

    return bestIdx
}

// DOM Performance Mode: Suspends CSS transitions & pauses expensive keyframe animations during active playback
function setPlaybackDomClass(active: boolean) {
    if (typeof document !== 'undefined' && document.body) {
        if (active) {
            document.body.classList.add('playback-active')
        } else {
            document.body.classList.remove('playback-active')
        }
    }
}

// Centralized fallback session minutes
const FALLBACK_MINUTES: number[] = MARKET_HOURS.FALLBACK_MINUTES as unknown as number[]

// High-performance calibrated speed matrix (Interval ms + Stride in index steps)
// Keeps tick rate within a smooth 60ms-100ms range (60 FPS without React UI starvation)
// while scaling the minute stride for lightning-fast session playback.
const SPEED_CONFIG = PLAYBACK_SPEED_CONFIG

const getInitialPlaySpeed = (): number => {
    if (typeof window === 'undefined') return DEFAULT_PLAYBACK_SPEED
    try {
        const stored = localStorage.getItem(STORAGE_KEYS.PLAYBACK_SPEED)
        if (stored) {
            const parsed = parseFloat(stored)
            if (!isNaN(parsed) && PLAYBACK_SPEED_CONFIG[parsed]) {
                return parsed
            }
        }
    } catch {
        // ignore
    }
    return DEFAULT_PLAYBACK_SPEED
}

export const usePlaybackStore = create<PlaybackState>((set, get) => {
    const loop = (timestamp: number) => {
        const state = get()
        if (!state.isPlaying) {
            stopLoop()
            return
        }

        // Backpressure Gating: If the previous frame has not completed rendering/painting,
        // yield to the browser without advancing snapshotMin, preventing render queue congestion.
        // Safety timeout: if rendering frame takes longer than 200ms, force release to prevent deadlock!
        if (state.isRenderingFrame) {
            if (timestamp - lastFrameDispatchTime > 200) {
                set({ isRenderingFrame: false })
            } else {
                animationFrameId = requestAnimationFrame(loop)
                return
            }
        }

        const speed = (typeof state.playSpeed === 'number' && state.playSpeed > 0) ? state.playSpeed : DEFAULT_PLAYBACK_SPEED
        const { interval, stride } = SPEED_CONFIG[speed] || {
            interval: Math.max(50, Math.round(500 / Math.max(0.01, speed))),
            stride: Math.max(1, Math.round(speed / 2)),
        }
        const elapsed = timestamp - lastTickTime

        if (elapsed >= interval) {
            lastTickTime = timestamp

            const times = state.availableMinutes.length > 0 ? state.availableMinutes : FALLBACK_MINUTES
            const currentIdx = findClosestIndex(times, state.snapshotMin)

            // If we have reached the end of available time, stop playback at the end (no auto-loop)
            if (currentIdx >= times.length - 1) {
                const lastMin = times[times.length - 1]
                set({ isPlaying: false, snapshotMin: lastMin ?? state.snapshotMin, isRenderingFrame: false })
                stopLoop()
                return
            }

            // Advance by calibrated stride for lightning-fast, smooth replay
            const nextIdx = Math.min(times.length - 1, currentIdx + stride)
            const nextMin = times[nextIdx]

            if (nextMin !== undefined && nextMin !== state.snapshotMin) {
                lastFrameDispatchTime = timestamp
                set({ snapshotMin: nextMin, isRenderingFrame: true })
            }
        }

        animationFrameId = requestAnimationFrame(loop)
    }

    const startLoop = () => {
        setPlaybackDomClass(true)
        if (!animationFrameId) {
            lastTickTime = performance.now() // Give 1 full interval (e.g. 100ms) for UI to paint Playing state smoothly before advancing
            animationFrameId = requestAnimationFrame(loop)
        }
    }

    const stopLoop = () => {
        setPlaybackDomClass(false)
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId)
            animationFrameId = null
        }
    }

    return {
        isPlaying: false,
        togglePlayback: () => {
            const state = get()
            const willPlay = !state.isPlaying
            if (willPlay) {
                const times = state.availableMinutes.length > 0 ? state.availableMinutes : FALLBACK_MINUTES
                const currentIdx = findClosestIndex(times, state.snapshotMin)
                // If user clicks play while already at the end, restart from beginning
                if (currentIdx >= times.length - 1 && times.length > 0) {
                    set({ isPlaying: true, snapshotMin: times[0]!, isRenderingFrame: false })
                } else {
                    set({ isPlaying: true, isRenderingFrame: false })
                }
                startLoop()
            } else {
                set({ isPlaying: false, isRenderingFrame: false })
                stopLoop()
            }
        },
        setPlaying: (playing: boolean) => {
            const state = get()
            if (playing) {
                const times = state.availableMinutes.length > 0 ? state.availableMinutes : FALLBACK_MINUTES
                const currentIdx = findClosestIndex(times, state.snapshotMin)
                if (currentIdx >= times.length - 1 && times.length > 0) {
                    set({ isPlaying: true, snapshotMin: times[0]!, isRenderingFrame: false })
                } else {
                    set({ isPlaying: true, isRenderingFrame: false })
                }
                startLoop()
            } else {
                set({ isPlaying: false, isRenderingFrame: false })
                stopLoop()
            }
        },
        snapshotMin: MARKET_HOURS.END_MINUTES, // Centralized market close default
        setSnapshotMin: (min: number) => set({ snapshotMin: min, isRenderingFrame: false }),
        availableMinutes: [],
        setAvailableMinutes: (mins: number[]) => set({ availableMinutes: mins }),
        isRenderingFrame: false,
        setRenderingFrame: (isRendering: boolean) => set({ isRenderingFrame: isRendering }),
        stepForward: () => {
            const state = get()
            const times = state.availableMinutes.length > 0 ? state.availableMinutes : FALLBACK_MINUTES
            const currentIdx = findClosestIndex(times, state.snapshotMin)
            const nextIdx = Math.min(times.length - 1, currentIdx + 1)
            const nextMin = times[nextIdx]
            if (nextMin !== undefined) {
                set({ snapshotMin: nextMin, isRenderingFrame: false })
            }
        },
        stepBackward: () => {
            const state = get()
            const times = state.availableMinutes.length > 0 ? state.availableMinutes : FALLBACK_MINUTES
            const currentIdx = findClosestIndex(times, state.snapshotMin)
            const prevIdx = Math.max(0, currentIdx - 1)
            const prevMin = times[prevIdx]
            if (prevMin !== undefined) {
                set({ snapshotMin: prevMin, isRenderingFrame: false })
            }
        },
        jumpTime: (minutesDelta: number) => {
            const state = get()
            const times = state.availableMinutes.length > 0 ? state.availableMinutes : FALLBACK_MINUTES
            const targetMin = state.snapshotMin + minutesDelta
            let closestIdx = findClosestIndex(times, targetMin)

            // If the jump resulted in no index movement (e.g. gap between snapshots), force at least one step in the direction
            if (times[closestIdx] === state.snapshotMin) {
                if (minutesDelta > 0 && closestIdx < times.length - 1) {
                    closestIdx++
                } else if (minutesDelta < 0 && closestIdx > 0) {
                    closestIdx--
                }
            }

            const chosen = times[closestIdx]
            if (chosen !== undefined) {
                set({ snapshotMin: chosen, isRenderingFrame: false })
            }
        },
        playSpeed: getInitialPlaySpeed(),
        setPlaySpeed: (speed: number) => {
            if (typeof window !== 'undefined') {
                try {
                    localStorage.setItem(STORAGE_KEYS.PLAYBACK_SPEED, speed.toString())
                } catch {
                    // ignore
                }
            }
            set({ playSpeed: speed })
        },
    }
})
